<template>
  <FreeBottomSheet
    :name="AI_CHAT_BOTTOM_SHEET"
    :is-handle-bar-visible="false"
    :is-closable="false"
    @onOpen="onOpenBottomSheet"
  >
    <template #header="{ hide }">
      <FreeHeader
        class="border-situational-lightgrey bg-background-neutral-primary border-b p-4"
      >
        <template #leftAction>
          <button
            v-if="!isHistoryMode"
            type="button"
            class="text-text-neutral-primary"
            @click="toggleHistoryMode"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M3 6C3 5.44772 3.44772 5 4 5H20C20.5523 5 21 5.44772 21 6C21 6.55228 20.5523 7 20 7H4C3.44772 7 3 6.55228 3 6Z"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M3 12C3 11.4477 3.44772 11 4 11H20C20.5523 11 21 11.4477 21 12C21 12.5523 20.5523 13 20 13H4C3.44772 13 3 12.5523 3 12Z"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M3 18C3 17.4477 3.44772 17 4 17H20C20.5523 17 21 17.4477 21 18C21 18.5523 20.5523 19 20 19H4C3.44772 19 3 18.5523 3 18Z"
              />
            </svg>
          </button>
          <button
            v-else
            type="button"
            class="text-text-neutral-primary"
            @click="toggleHistoryMode"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 19L3 12M3 12L10 5M3 12L21 12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </template>
        <div class="flex items-center justify-center space-x-2">
          <picture>
            <source srcset="./assets/smile.webp" type="image/webp" />
            <img
              class="h-6 w-6"
              src="./assets/smile.png"
              width="24"
              height="24"
            />
          </picture>
          <p class="text-text-neutral-primary font-medium">
            {{ t('aiChat.title') }}
          </p>
        </div>
        <template #rightAction>
          <button type="button" @click="hide">
            <img
              class="h-6 w-6"
              src="@/assets/images/close-outlined-icon.svg"
              alt=""
            />
          </button>
        </template>
      </FreeHeader>
    </template>
    <template #default="{ hide }">
      <div
        class="bg-background-neutral-primary flex h-full min-h-[calc(100vh-var(--status-bar-height))] flex-col space-y-4 overflow-y-auto px-4 pb-64 pt-4"
      >
        <div v-if="isHistoryMode" class="flex flex-col space-y-2">
          <FreeButton
            class="border-situational-lightgrey text-text-neutral-primary mb-2 flex w-full items-center justify-center rounded-2xl border px-4 py-3 text-sm font-medium disabled:opacity-60"
            :disabled="isSessionCreating || isSessionSwitching"
            @click="handleCreateSessionClick"
          >
            <span v-if="isSessionCreating">{{
              t('aiChat.creatingSession')
            }}</span>
            <span v-else>{{ t('aiChat.newSession') }}</span>
          </FreeButton>
          <button
            v-for="historySession in sessionHistory"
            :key="historySession.session_id"
            type="button"
            class="border-situational-lightgrey flex w-full items-center justify-between rounded-[20px] border px-4 py-3 disabled:opacity-70"
            :disabled="isSessionSwitching || isSessionCreating"
            @click="handleHistorySessionClick(historySession)"
          >
            <div class="flex flex-col items-start space-y-0.5 text-left">
              <span class="text-situational-darkgrey text-2xs">
                {{
                  dateUtils.format(
                    historySession.updated_at,
                    'DD.MM.YYYY HH:mm',
                  )
                }}
              </span>
              <span class="text-situational-darkgrey text-sm">
                {{
                  historySession.session_id === sessionId
                    ? t('aiChat.currentSession')
                    : t('aiChat.session')
                }}
              </span>
              <span class="text-text-neutral-primary font-medium">
                {{ formatSessionTitle(historySession) }}
              </span>
            </div>
            <div
              v-if="
                switchingSessionId === historySession.session_id &&
                isSessionSwitching
              "
              class="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"
            ></div>
          </button>
          <p
            v-if="sessionHistory.length === 0"
            class="text-situational-darkgrey text-sm"
          >
            {{ t('aiChat.noSessions') }}
          </p>
        </div>
        <template v-else>
          <p class="text-text-neutral-primary text-lg font-semibold">
            {{ t('aiChat.description') }}
          </p>
          <div v-if="messages.length === 0" class="flex flex-col space-y-2">
            <button
              v-for="suggestion in suggestions"
              :key="suggestion.id || suggestion.text"
              type="button"
              class="border-situational-lightgrey text-text-neutral-primary w-fit rounded-2xl border px-3 py-2 text-left text-sm"
              @click="handleSuggestionClickLocal(suggestion)"
            >
              {{ suggestion.text }}
            </button>
          </div>
          <div v-if="isOpened" class="flex flex-col space-y-4">
            <div
              v-for="(message, index) in messages"
              v-show="message.content.type !== 'suggestions'"
              :key="index"
              class="flex"
              :class="
                message.sender === MESSAGE_USER
                  ? 'justify-end'
                  : 'justify-start'
              "
            >
              <CustomerMessage
                v-if="message.sender === MESSAGE_USER"
                class="max-w-[60%]"
                :message="message"
                @onClose="hide"
              />
              <AssistantMessage
                v-else
                class="max-w-[90%]"
                :message="message"
                @onCashbackInfoClick="onCashbackInfoClick"
              />
            </div>
            <div v-if="isFetching" class="my-4 flex items-end space-x-1">
              <div
                v-for="i in 3"
                :key="i"
                class="animate-wave bg-situational-lightgrey h-2 w-2 rounded-full"
                :style="{ animationDelay: `${(i - 1) * 0.15}s` }"
              ></div>
            </div>
            <p
              v-if="thinkingStatusText"
              class="text-situational-darkgrey text-xs"
            >
              {{ thinkingStatusText }}
            </p>
            <FreeButton
              v-if="isError"
              class="border-situational-lightgrey text-text-neutral-primary mt-2 w-fit rounded-xl border px-3 py-2 text-xs font-medium disabled:opacity-60"
              :disabled="isFetching"
              @click="handleRetryClick"
            >
              {{ t('aiChat.retry') }}
            </FreeButton>
          </div>
          <div
            class="bg-background-neutral-primary fixed inset-x-0 bottom-0 z-30 px-2 pb-7"
          >
            <div v-if="messages.length > 0" class="my-2 flex flex-wrap gap-2">
              <button
                v-for="suggestion in suggestions.slice(0, 3)"
                :key="suggestion.id || suggestion.text"
                type="button"
                class="border-situational-lightgrey text-text-neutral-primary w-fit rounded-2xl border px-3 py-2 text-left text-xs"
                @click="handleSuggestionClickLocal(suggestion)"
              >
                {{ suggestion.text }}
              </button>
            </div>
            <AiChatForm
              :message-text="messageText"
              :is-loading="isFetching || isDisabled"
              @onInput="handleInputLocal"
              @onSubmit="handleSubmitLocal"
            />
          </div>
        </template>
      </div>
      <CashbackBottomSheet :name="CASHBACK_BOTTOMSHEET" />
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { ref, onMounted, nextTick } from 'vue';
  import { useI18n } from 'vue-i18n';
  import {
    FreeBottomSheet,
    FreeHeader,
    CashbackBottomSheet,
    FreeButton,
  } from 'freedom-shared';
  import { controllerModel, dateUtils } from 'shared';

  import { AI_CHAT_BOTTOM_SHEET, MESSAGE_USER } from '../config';
  import AiChatForm from './AiChatForm.vue';
  import { CustomerMessage, AssistantMessage } from './messages';
  import { useAiChat } from '../model';
  import type { StreamSuggestionItem, AiChatSessionListItem } from '../api';

  const CASHBACK_BOTTOMSHEET = 'cashback-bottom-sheet';
  const isOpened = ref(false);
  const isHistoryMode = ref(false);
  const bottomSheet = controllerModel.useBottomSheet();
  const { t } = useI18n();
  const {
    messages,
    isFetching,
    isError,
    isDisabled,
    messageText,
    suggestions,
    thinkingStatusText,
    sessionId,
    sessionHistory,
    isSessionSwitching,
    switchingSessionId,
    isSessionCreating,
    handleInput,
    handleSubmit,
    handleSuggestionClick,
    retryLastMessage,
    switchSession,
    createNewSession,
    initializeChat,
  } = useAiChat();

  const handleInputLocal = (value: string) => {
    handleInput(value);
  };

  // Прокрутка к низу чата после отправки сообщения
  const scrollToBottom = () => {
    nextTick(() => {
      const chatContainer = document.getElementById(AI_CHAT_BOTTOM_SHEET);
      if (chatContainer) {
        chatContainer.scrollTo({
          top: chatContainer.scrollHeight,
          behavior: 'smooth',
        });
      }
    });
  };

  const onOpenBottomSheet = () => {
    isOpened.value = true;
    if (messages.value.length > 0) {
      scrollToBottom();
    }
  };

  const handleSubmitLocal = async () => {
    setTimeout(() => {
      scrollToBottom();
    });
    await handleSubmit(scrollToBottom);
    setTimeout(() => {
      scrollToBottom();
    });
  };

  const handleSuggestionClickLocal = (suggestion: StreamSuggestionItem) => {
    handleSuggestionClick(suggestion);
    handleSubmitLocal();
  };

  const handleRetryClick = async () => {
    await retryLastMessage(scrollToBottom);
    setTimeout(() => {
      scrollToBottom();
    });
  };

  const onCashbackInfoClick = () => {
    bottomSheet.show(CASHBACK_BOTTOMSHEET);
  };

  const formatSessionTitle = (session: AiChatSessionListItem) => {
    return session.title || t('aiChat.noContext');
  };

  const handleCreateSessionClick = async () => {
    const isCreated = await createNewSession();

    if (isCreated) {
      isHistoryMode.value = false;
    }
  };

  const handleHistorySessionClick = async (
    historySession: AiChatSessionListItem,
  ) => {
    const isSwitched = await switchSession(historySession.session_id);

    if (isSwitched) {
      isHistoryMode.value = false;
      if (messages.value.length > 0) {
        scrollToBottom();
      }
    }
  };

  const scrollToTop = () => {
    nextTick(() => {
      const chatContainer = document.getElementById(AI_CHAT_BOTTOM_SHEET);
      if (chatContainer) {
        chatContainer.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  const toggleHistoryMode = () => {
    isHistoryMode.value = !isHistoryMode.value;
    if (isHistoryMode.value) {
      scrollToTop();
    } else {
      scrollToBottom();
    }
  };

  onMounted(() => {
    initializeChat();
  });
</script>

<style scoped>
  @keyframes wave {
    0%,
    60%,
    100% {
      transform: translateY(0);
    }
    30% {
      transform: translateY(-4px);
    }
  }

  .animate-wave {
    animation: wave 1s ease-in-out infinite;
  }
</style>
